/**
 * Idle-session timeout helpers (Phase 4).
 *
 * Tracks user activity (mousemove / keydown / click) and fires a callback
 * after a configurable idle window. A separate absolute timeout enforces a
 * hard ceiling regardless of activity.
 */

import { useEffect, useRef } from 'react';

export const DEFAULT_IDLE_MS = 15 * 60 * 1000;
export const DEFAULT_ABSOLUTE_MS = 8 * 60 * 60 * 1000;

/** Activity events worth resetting the idle clock on. */
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
  'click',
];

interface IdleWatcherOptions {
  /** Idle window in ms; after this the timeout fires. Default 15min. */
  idleMs?: number;
  /** Absolute cap in ms from `sessionStart`; fires even with activity. Default 8h. */
  absoluteMs?: number;
  /** When the user has been idle long enough. */
  onIdle: (reason: 'idle' | 'absolute') => void;
  /** Wall-clock timestamp when this session started (Date.now()). */
  sessionStart: number;
  /** Throttle window for activity resets, to keep mousemove cheap. */
  throttleMs?: number;
}

/**
 * Install activity listeners and return an `uninstall` function.
 * Callers (React components) typically prefer `useIdleTimeout` below.
 */
export function installIdleWatcher(opts: IdleWatcherOptions): () => void {
  const idleMs = opts.idleMs ?? DEFAULT_IDLE_MS;
  const absoluteMs = opts.absoluteMs ?? DEFAULT_ABSOLUTE_MS;
  const throttleMs = opts.throttleMs ?? 1000;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let absoluteTimer: ReturnType<typeof setTimeout> | null = null;
  let lastReset = 0;
  let cancelled = false;

  const fire = (reason: 'idle' | 'absolute') => {
    if (cancelled) return;
    cancelled = true;
    cleanup();
    opts.onIdle(reason);
  };

  const cleanup = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (absoluteTimer) clearTimeout(absoluteTimer);
    idleTimer = null;
    absoluteTimer = null;
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, onActivity, { capture: true } as EventListenerOptions);
    }
  };

  const resetIdleTimer = () => {
    if (cancelled) return;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => fire('idle'), idleMs);
  };

  const onActivity = () => {
    const now = Date.now();
    if (now - lastReset < throttleMs) return;
    lastReset = now;
    resetIdleTimer();
  };

  // Absolute cap: ms remaining from sessionStart + absoluteMs.
  const absoluteRemaining = Math.max(0, opts.sessionStart + absoluteMs - Date.now());
  if (absoluteRemaining === 0) {
    // Already over the cap — fire on next tick so React state can drain.
    setTimeout(() => fire('absolute'), 0);
    return () => cleanup();
  }
  absoluteTimer = setTimeout(() => fire('absolute'), absoluteRemaining);

  resetIdleTimer();
  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, onActivity, { passive: true, capture: true });
  }

  return () => cleanup();
}

/**
 * React hook: idle/absolute auto-signout. Pass `enabled=false` to disable
 * (e.g. when the user is on the LoginScreen).
 */
export function useIdleTimeout(
  enabled: boolean,
  onIdle: (reason: 'idle' | 'absolute') => void,
  options?: Pick<IdleWatcherOptions, 'idleMs' | 'absoluteMs'>,
): void {
  const startRef = useRef<number>(Date.now());
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  // Reset session start whenever we transition from disabled → enabled.
  useEffect(() => {
    if (enabled) {
      startRef.current = Date.now();
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    const uninstall = installIdleWatcher({
      sessionStart: startRef.current,
      onIdle: (reason) => onIdleRef.current(reason),
      idleMs: options?.idleMs,
      absoluteMs: options?.absoluteMs,
    });
    return uninstall;
  }, [enabled, options?.idleMs, options?.absoluteMs]);
}
